import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { useMobile } from '../hooks/useMobile';

interface LogsModalProps {
  pod: any;
  onClose: () => void;
  theme?: 'dark' | 'light';
}

const LINE_OPTIONS = [50, 100, 200, 300, 500, 1000, 2000];

// Stable preset definitions — durations only, no absolute timestamps.
// (Previously the "Since" buttons computed `Date.now() - X` inline on every
// render, so the selected button's value drifted a few ms out of sync with
// the stored `since` state on the very next render and never showed as
// selected again. The actual ISO timestamp is now computed fresh at fetch
// time in `load()`, which also means "5m" always means "the last 5 minutes
// as of now" rather than a stale window from whenever it was first clicked.)
const TIME_PRESETS: { label: string; ms: number | null }[] = [
  { label: 'All', ms: null },
  { label: '5m',  ms: 5   * 60_000 },
  { label: '15m', ms: 15  * 60_000 },
  { label: '30m', ms: 30  * 60_000 },
  { label: '1h',  ms: 3_600_000 },
  { label: '3h',  ms: 3 * 3_600_000 },
  { label: '6h',  ms: 6 * 3_600_000 },
];

// Colour a single log line based on its content (works in both modes)
function lineColor(line: string, dark: boolean): string {
  const l = line.toLowerCase();
  if (/\b(error|err|fatal|exception|panic|fail|critical)\b/.test(l)) return dark ? '#fca5a5' : '#b91c1c';
  if (/\b(warn|warning)\b/.test(l))       return dark ? '#fde68a' : '#92400e';
  if (/\b(info|informati)\b/.test(l))     return dark ? '#93c5fd' : '#1d4ed8';
  if (/\b(debug|trace)\b/.test(l))        return dark ? '#6b7280' : '#9ca3af';
  if (/\b(success|started|ready|ok)\b/.test(l)) return dark ? '#86efac' : '#15803d';
  return dark ? '#cbd5e1' : '#334155';
}

// Highlight timestamp prefix differently
function renderLine(line: string, dark: boolean, n: number, readerMode: boolean) {
  // Kubernetes timestamps: 2024-01-15T10:30:00.000000000Z
  const tsMatch = line.match(/^(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\s(.*)/s);
  const ts   = tsMatch ? tsMatch[1] : null;
  const body = tsMatch ? tsMatch[2] : line;
  const col  = lineColor(line, dark);
  const tsSub = dark ? '#475569' : '#94a3b8';
  const numCol = dark ? '#374151' : '#d1d5db';
  const numTextCol = dark ? '#4b5563' : '#9ca3af';

  if (readerMode) {
    return (
      <div key={n} style={{ display: 'flex', gap: 0, padding: '2px 0', borderBottom: `1px solid ${dark ? '#1e293b' : '#f1f5f9'}` }}>
        <span style={{ minWidth: 44, color: numTextCol, fontSize: 11, background: numCol + '55', padding: '0 8px', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: 'monospace' }}>
          {n}
        </span>
        {ts && (
          <span style={{ color: tsSub, fontSize: 11, padding: '1px 8px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
            {ts.replace('T', ' ').replace(/\.\d+Z$/, 'Z')}
          </span>
        )}
        <span style={{ color: col, padding: '1px 8px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1 }}>
          {body}
        </span>
      </div>
    );
  }

  return (
    <div key={n} style={{ display: 'flex', gap: 0, padding: '1px 0' }}>
      {ts && (
        <span style={{ color: tsSub, fontSize: 11, paddingRight: 8, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
          {ts.replace('T', ' ').replace(/\.\d+Z$/, 'Z')}
        </span>
      )}
      <span style={{ color: col, fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre', fontFamily: 'monospace' }}>
        {body}
      </span>
    </div>
  );
}

export function LogsModal({ pod, onClose, theme = 'dark' }: LogsModalProps) {
  const [rawLogs, setRawLogs]   = useState<string[]>([]);
  const [lines,   setLines]     = useState(200);
  const [sincePreset, setSincePreset] = useState('All');
  const [loading, setLoading]   = useState(false);
  const [search,  setSearch]    = useState('');
  const [readerMode, setReaderMode] = useState(false);
  const [copied,  setCopied]    = useState(false);
  const [totalLines, setTotalLines] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const mobile    = useMobile();

  const dark = theme === 'dark';
  const bg   = dark ? '#0a0f1a' : '#f8fafc';
  const card = dark ? '#111827' : '#ffffff';
  const bord = dark ? '#1e293b' : '#e2e8f0';
  const sub  = dark ? '#64748b' : '#94a3b8';
  const textColor = dark ? '#e2e8f0' : '#1e293b';

  const btnBase: React.CSSProperties = {
    border: `1px solid ${bord}`,
    borderRadius: 6,
    padding: '5px 12px',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    background: dark ? '#1e293b' : '#f1f5f9',
    color: sub,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/pods/${pod.namespace}/${pod.name}/logs?lines=${lines}`;
      const preset = TIME_PRESETS.find(t => t.label === sincePreset);
      if (preset?.ms) {
        const sinceIso = new Date(Date.now() - preset.ms).toISOString();
        url += `&since=${encodeURIComponent(sinceIso)}`;
      }
      const res = await api(url);
      if (res.error) {
        setRawLogs([`ERROR: ${res.error}`]);
        setTotalLines(1);
      } else {
        const all = (res.logs || '').split('\n').filter((l: string) => l.length > 0);
        setRawLogs(all);
        setTotalLines(res.lines || all.length);
      }
    } catch (e: any) {
      setRawLogs([`Failed to fetch logs: ${e.message}`]);
      setTotalLines(1);
    } finally {
      setLoading(false);
    }
  }, [pod, lines, sincePreset]);

  // Reload whenever lines OR since changes
  useEffect(() => { load(); }, [load]);

  // Scroll to bottom on new load in normal mode
  useEffect(() => {
    if (!readerMode && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [rawLogs, readerMode]);

  const filteredLogs = search
    ? rawLogs.filter(l => l.toLowerCase().includes(search.toLowerCase()))
    : rawLogs;

  function copyLogs() {
    navigator.clipboard.writeText(rawLogs.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  const errorCount = rawLogs.filter(l => /\b(error|err|fatal|exception|panic)\b/i.test(l)).length;
  const warnCount  = rawLogs.filter(l => /\b(warn|warning)\b/i.test(l)).length;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: mobile ? 0 : 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: card, borderRadius: mobile ? 0 : 14, width: mobile ? '100%' : '92vw', maxWidth: 1200, height: mobile ? '100vh' : '90vh', display: 'flex', flexDirection: 'column', border: `1px solid ${bord}`, boxShadow: '0 32px 64px rgba(0,0,0,0.7)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${bord}`, display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
          {/* Row 1: title + close */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontFamily: 'monospace', fontSize: mobile ? 12 : 14, color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: mobile ? 180 : 500 }}>
                {pod.name}
              </div>
              <div style={{ color: sub, fontSize: 11, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>{pod.namespace}</span>
                {totalLines > 0 && (
                  <span>
                    {filteredLogs.length} line{filteredLogs.length !== 1 ? 's' : ''}
                    {search ? ` (filtered from ${rawLogs.length})` : ''}
                    {!search && sincePreset === 'All' && rawLogs.length < lines ? ' (all available — the container has no more)' : ''}
                  </span>
                )}
                {errorCount > 0 && <span style={{ color: '#fca5a5', fontWeight: 700 }}>{errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
                {warnCount  > 0 && <span style={{ color: '#fde68a', fontWeight: 700 }}>{warnCount} warning{warnCount  !== 1 ? 's' : ''}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
              {/* Reader mode toggle */}
              <button
                onClick={() => setReaderMode(r => !r)}
                style={{ ...btnBase, background: readerMode ? '#1e40af' : (dark ? '#1e293b' : '#f1f5f9'), color: readerMode ? '#93c5fd' : sub }}
                title="Toggle reader mode — line numbers, coloured severity, larger text"
              >
                {readerMode ? 'Reader On' : 'Reader'}
              </button>
              <button onClick={copyLogs} style={{ ...btnBase, color: copied ? '#86efac' : sub }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={load} disabled={loading} style={{ ...btnBase, background: '#2563eb', color: '#fff', border: 'none', opacity: loading ? 0.7 : 1 }}>
                {loading ? '…' : 'Refresh'}
              </button>
              <button onClick={onClose} style={{ ...btnBase, fontSize: 18, padding: '4px 10px' }}>×</button>
            </div>
          </div>

          {/* Row 2: filters */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Time presets */}
            <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: sub, fontWeight: 600, whiteSpace: 'nowrap' }}>Since:</span>
              {TIME_PRESETS.map(t => (
                <button
                  key={t.label}
                  onClick={() => setSincePreset(t.label)}
                  style={{
                    ...btnBase,
                    padding: '3px 8px', fontSize: 11,
                    background: sincePreset === t.label ? '#2563eb' : (dark ? '#0f172a' : '#f1f5f9'),
                    color: sincePreset === t.label ? '#fff' : sub,
                    border: sincePreset === t.label ? '1px solid #2563eb' : `1px solid ${bord}`,
                  }}
                >{t.label}</button>
              ))}
            </div>

            {/* Divider */}
            <span style={{ color: bord, fontSize: 16, lineHeight: 1 }}>|</span>

            {/* Lines */}
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: sub, fontWeight: 600, whiteSpace: 'nowrap' }}>Lines:</span>
              {LINE_OPTIONS.map(n => (
                <button
                  key={n}
                  onClick={() => setLines(n)}
                  style={{
                    ...btnBase,
                    padding: '3px 8px', fontSize: 11,
                    background: lines === n ? '#7c3aed' : (dark ? '#0f172a' : '#f1f5f9'),
                    color: lines === n ? '#fff' : sub,
                    border: lines === n ? '1px solid #7c3aed' : `1px solid ${bord}`,
                  }}
                >{n}</button>
              ))}
            </div>

            {/* Search */}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter logs…"
              style={{
                marginLeft: 'auto',
                background: dark ? '#0f172a' : '#f8fafc',
                border: `1px solid ${bord}`,
                color: textColor,
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 12,
                width: mobile ? '100%' : 200,
              }}
            />
          </div>
        </div>

        {/* ── Log content ── */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            background: bg,
            borderRadius: mobile ? 0 : '0 0 14px 14px',
            padding: readerMode ? 0 : (mobile ? '10px 12px' : '14px 18px'),
            fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace',
          }}
        >
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: sub, fontSize: 13 }}>Loading logs…</div>
          ) : filteredLogs.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: sub, fontSize: 13 }}>
              {rawLogs.length === 0 ? 'No logs available for this pod' : 'No lines match your filter'}
            </div>
          ) : (
            filteredLogs.map((line, i) => renderLine(line, dark, i + 1, readerMode))
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
}
